/**
 * The persistent open-provisioning-window banner (brief §4H).
 *
 * Shown across the whole ops app while any window is open — an open window
 * is a deliberate, temporary widening of the enrollment surface and must be
 * impossible to forget. Polls the active-window list; hidden without the
 * provisioning.view permission (the query never runs).
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getActiveWindows } from "@pbxmgr/api";

import { usePermission } from "../auth/usePermission";

export function WindowBanner(): ReactNode {
  const canView = usePermission("provisioning.view");
  const windows = useQuery({
    queryKey: ["prov", "windows"],
    queryFn: getActiveWindows,
    enabled: canView,
    refetchInterval: 30_000,
  });

  const active = windows.data?.items ?? [];
  if (active.length === 0) return null;

  const soonest = active
    .map((w) => w.expires_at)
    .filter((e): e is string => e !== null)
    .sort()[0];

  return (
    <div className="window-banner" role="status">
      <strong>
        Open provisioning window{active.length > 1 ? `s (${active.length})` : ""}
      </strong>{" "}
      — enrollment is temporarily open ({active[0].scope_kind}
      {active.length > 1 ? ", …" : ""})
      {soonest ? `, expires ${new Date(soonest).toLocaleTimeString()}` : ""}.{" "}
      <Link to="/devices">Manage</Link>
    </div>
  );
}
